import {
  chainStages,
  FULL_CHAIN,
  STAGE_DESCRIPTIONS,
  STAGE_LABELS,
  type AssessmentStage,
  type ReviewChain,
} from '@cfl/shared';

import { cn } from '@/lib/utils';

/**
 * This quarter's review chain.
 *
 * Shows every stage at once, including the ones not reached, because "where is this
 * and who is it waiting on" is the question everyone opens an assessment to answer.
 * The active stage is blue, not amber - sitting at HR review is on track, and
 * colouring normal progress as a warning is how a dashboard stops meaning anything.
 */
export function StageTracker({
  completed,
  active,
  overdue = false,
  actors = {},
  horizontal = false,
  chain = FULL_CHAIN,
}: {
  completed: AssessmentStage[];
  active: AssessmentStage | null;
  /** Only ever true for the manager stage, past its 3-day SLA. */
  overdue?: boolean;
  actors?: Partial<Record<AssessmentStage, string>>;
  /** Which optional stages this quarter runs. Defaults to all five. */
  chain?: ReviewChain;
  /** Lay the stages left-to-right from `sm` up. Stays vertical on mobile either way. */
  horizontal?: boolean;
}) {
  const stages = chainStages(chain);

  return (
    <ol className={cn('flex flex-col gap-0', horizontal && 'sm:flex-row')}>
      {stages.map((stage, index) => {
        const isDone = completed.includes(stage);
        const isActive = active === stage;
        const isLast = index === stages.length - 1;

        return (
          <li
            key={stage}
            className={cn('flex gap-3', horizontal && 'sm:flex-1 sm:flex-col sm:gap-2')}
          >
            <div
              className={cn('flex flex-col items-center', horizontal && 'sm:w-full sm:flex-row')}
            >
              <span
                className={cn(
                  'mt-1 size-3 shrink-0 rounded-full ring-4 ring-background',
                  isDone && 'bg-success',
                  isActive && !overdue && 'bg-primary',
                  isActive && overdue && 'bg-destructive',
                  !isDone && !isActive && 'bg-muted',
                )}
                aria-hidden
              />
              {!isLast && (
                <span
                  className={cn(
                    'mt-1 w-px flex-1',
                    horizontal && 'sm:mx-2 sm:mt-0 sm:h-px sm:w-auto',
                    isDone ? 'bg-success/40' : 'bg-border',
                  )}
                  aria-hidden
                />
              )}
            </div>

            <div className={cn('flex-1 pb-5', isLast && 'pb-0', horizontal && 'sm:pr-4 sm:pb-0')}>
              <div className="flex flex-wrap items-center gap-2">
                <p
                  className={cn(
                    'text-sm',
                    isActive ? 'font-semibold' : isDone ? 'font-medium' : 'text-muted-foreground',
                  )}
                >
                  {STAGE_LABELS[stage]}
                </p>
                {isActive && (
                  <span
                    className={cn(
                      'rounded-full px-2 py-0.5 text-[11px] font-semibold',
                      overdue
                        ? 'bg-[hsl(0_84%_60%/0.13)] text-[hsl(0_70%_45%)]'
                        : 'bg-[hsl(217_91%_60%/0.14)] text-[hsl(217_80%_45%)]',
                    )}
                  >
                    {overdue ? 'Overdue' : 'In progress'}
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                {actors[stage] ?? STAGE_DESCRIPTIONS[stage]}
              </p>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
